import React, { Fragment, useState, useEffect } from "react";
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCommentAlt, faBicycle, faMapMarkerAlt, faCheckCircle, faShoppingBasket, faEnvelope, faSearch, faTruck, faFire, faClipboardList, faClock, faPhone, faTimes } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import LoadingOverlay from "react-loading-overlay-ts";
import DNALoader from "../../../components/Loaders/DnaLoader";
import Alerts from "../../../components/Alerts/alerts";
import OrderService from "../order.service.js";
import AuthService from "../../Authentication/auth.service";
import DiscountCouponService from "../../DiscountCoupon/Discountcoupons.service.js";
import Header from './Header';

const Orders = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [branch, setBranch] = useState('');
  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [trackingData, setTrackingData] = useState([]);
  const [isTrackingLoading, setIsTrackingLoading] = useState(true);
  const [trackingError, setTrackingError] = useState(null);
  const formatStatus = (status) => {
    return status
      .toLowerCase()
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  useEffect(() => {
    const fetchTrackingData = async () => {
      if (orderDetails) {
        setIsTrackingLoading(true);
        try {
          const lalamove_orderID = orderDetails.lalamove_orderID;
          const response = await OrderService.GetOrderTrack(lalamove_orderID);
          if (response.data.message === "Unauthorized") {
            AuthService.logout();
            navigate("/login");
            window.location.reload();
          } else {
            console.log(response.data.orderData)
            setTrackingData(response.data.orderData);
          }
        } catch (err) {
          console.log("err",)
          setTrackingError('Failed to fetch tracking data');
        } finally {
          setIsTrackingLoading(false);
        }
      }
    };

    fetchTrackingData();
  }, [orderDetails, activeTab]);

  useEffect(() => {
    const fetchOrderDetails = async () => {
      setLoading(true);
      try {
        const orderId = location.state.order_id;
        const response = await OrderService.getOrderDetails(orderId);
        if (response.data.message === "Unauthorized") {
          AuthService.logout();
          navigate("/login");
          window.location.reload();
        } else {
          // console.log("Order Details  : ", response.data.orderData[0]);
          setOrderDetails(response?.data?.orderData?.[0]);
        }
      } catch (error) {
        console.error('Error fetching order details:', error);
        Alerts.swalErrorAlert(t("Failed to fetch order details."));
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, []);

  if (loading) {
    return (
      <Fragment>
        <div className="app-main__outer">
          <div className="app-main__inner">
            <TransitionGroup>
              <CSSTransition component="div" classNames="TabsAnimation" appear={true} timeout={0} enter={false} exit={false}>

                <LoadingOverlay tag="div" active={loading}
                  styles={{ overlay: (base) => ({ ...base }) }}
                  spinner={<DNALoader />}>
                  <div style={{ height: '100vh' }}></div>
                </LoadingOverlay>
              </CSSTransition>
            </TransitionGroup>
          </div>
        </div>
      </Fragment >
    );
  }
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    }).replace(/,/, ' -');
  };

  const renderDetailsContent = () => (
    <>
      {/* Delivery details */}
      {orderDetails?.address !== '' && orderDetails?.distance !== '' && orderDetails?.distance_unit !== '' &&
        <div style={{
          backgroundColor: 'white',
          borderRadius: '10px',
          padding: '20px',
          marginBottom: '20px',
          boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{ marginTop: 0 }}>Delivery</h2>
          <div style={{ display: 'flex', marginBottom: '10px' }}>
            <div style={{ borderRadius: '50%', width: '100px', height: '100px', marginRight: '10px' }}>
              <img src="https://www.altitudeprojects.net/js-mp/web/assets/img/splash3.png" alt="Map" style={{ width: 'auto', height: '100%', objectFit: 'cover' }} />
            </div>
            <div style={{ marginBottom: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
                <FontAwesomeIcon icon={faBicycle} style={{ marginRight: '10px', color: '#e74c3c' }} />
                <span>{parseFloat(orderDetails?.distance).toFixed(2) / 1000}km</span>
              </div>
              <p style={{ margin: '0 0 0 24px' }}>{orderDetails?.address}</p>
            </div>
          </div>
          <p style={{ textAlign: 'right', fontWeight: 'bold', margin: 0 }}>{orderDetails?.Calculations?.deliveryTipFee?.toFixed(2)}</p>
        </div>
      }

      {/* Order items and summary */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '10px',
        padding: '20px',
        marginBottom: '20px',
        boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
      }}>
        {orderDetails?.cartDetails.map((item, index) => {
          // Calculate total variation price
          const totalVariationPrice = item.variations?.reduce((sum, variation) => 
            sum + (parseFloat(variation.variation_price) || 0), 0) || 0;
          
          // Calculate total price including variations and quantity
          const totalPrice = (parseFloat(item.price_per_item) + totalVariationPrice) * item.quantity;

          return (
            <div key={index} style={{ marginBottom: '15px' }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ width: '50px', height: '50px', backgroundColor: '#f0f0f0', marginRight: '10px', borderRadius: '5px' }}>
                  <img src={item.item.item_image} alt={item.item.item_name} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '5px' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ margin: 0, fontWeight: 'bold' }}>{item.item.item_name}</p>
                  <p style={{ margin: 0, color: '#666' }}>
                   {parseFloat(item.price_per_item).toFixed(2)} x {item.quantity}
                  </p>
                </div>
                <p style={{ margin: 0, fontWeight: 'bold' }}>RM {totalPrice.toFixed(2)}</p>
              </div>
              {item.variations && item.variations.length > 0 && (
                <div style={{ marginLeft: '60px', marginTop: '5px' }}>
                  <p style={{ margin: '0 0 5px 0', color: '#666', fontSize: '0.9em' }}>Variations:</p>
                  {item.variations.map((variation, vIndex) => (
                    <p key={vIndex} style={{ margin: '0 0 3px 10px', fontSize: '0.85em', color: '#666' }}>
                      {variation.variation_name}: {variation.variation_type_name} (RM {parseFloat(variation.variation_price).toFixed(2)} x {item.quantity})
                    </p>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        <hr style={{ margin: '15px 0' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', margin: '5px 0' }}>
          <p style={{ margin: 0 }}>Subtotal</p>
          <p style={{ margin: 0 }}>{orderDetails?.Calculations.subTotal.toFixed(2)}</p>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', margin: '5px 0' }}>
          <p style={{ margin: 0 }}>Discount</p>
          <p style={{ margin: 0 }}>- {orderDetails?.Calculations.discount.toFixed(2)}</p>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', margin: '5px 0' }}>
          <p style={{ margin: 0 }}>{orderDetails?.Calculations.sst_dynamic_label} ({orderDetails?.Calculations.sst_percentage})</p>
          <p style={{ margin: 0 }}>{orderDetails?.Calculations.sst.toFixed(2)}</p>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', margin: '5px 0' }}>
          <p style={{ margin: 0 }}>Delivery fees</p>
          <p style={{ margin: 0 }}>{orderDetails?.Calculations.deliveryTipFee.toFixed(2)}</p>
        </div>
        <hr style={{ margin: '15px 0' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold' }}>
          <p style={{ margin: 0 }}>Total</p>
          <p style={{ margin: 0 }}>{orderDetails?.Calculations.grandTotal.toFixed(2)}</p>
        </div>
      </div>

      {/* Seller details */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '10px',
        padding: '20px',
        boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
      }}>
        <h2 style={{ marginTop: 0 }}>Seller Details:</h2>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{
            width: '50px',
            height: '50px',
            backgroundColor: '#8e44ad',
            borderRadius: '50%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            color: 'white',
            marginRight: '15px',
            fontSize: '24px',
            fontWeight: 'bold'
          }}>
            {orderDetails?.businessDetails[0]?.name[0]}
          </div>
          <div>
            <p style={{ margin: '0', fontWeight: 'bold' }}>{orderDetails?.businessDetails[0]?.name}</p>
            <p style={{ margin: '5px 0', color: '#e74c3c' }}>
              <FontAwesomeIcon icon={faEnvelope} style={{ marginRight: '5px' }} />
              {orderDetails?.businessDetails[0]?.email}
            </p>
          </div>
        </div>
      </div>
    </>
  );

  const renderTrackingContentDEMO = () => {
    if (isTrackingLoading) {
      return <div>Loading tracking information...</div>;
    }

    if (trackingError) {
      return <div>{trackingError}</div>;
    }

    if (trackingData.length === 0) {
      return <div>No tracking information available</div>;
    }

    return (
      <div style={{
        backgroundColor: 'white',
        borderRadius: '10px',
        padding: '20px',
        boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
      }}>
        {trackingData.map((step, index) => (
          <div key={index} style={{
            display: 'flex',
            marginBottom: '20px',
            position: 'relative'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              backgroundColor: '#e74c3c',
              borderRadius: '50%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginRight: '20px',
              zIndex: 1
            }}>
              <FontAwesomeIcon icon={getIconForStatus(step.status)} style={{ color: 'white' }} />
            </div>
            {index !== trackingData.length - 1 && (
              <div style={{
                position: 'absolute',
                left: '20px',
                top: '40px',
                bottom: '-20px',
                width: '2px',
                borderLeft: '2px dashed #e74c3c',
                zIndex: 0
              }} />
            )}
            <div style={{ flex: 1 }}>
              <h3 style={{ margin: '0 0 5px 0', color: '#333' }}>{formatStatus(step.status)}</h3>
              <p style={{ margin: '0', color: '#e74c3c' }}>
                <FontAwesomeIcon icon={faClock} style={{ marginRight: '5px' }} />
                {step.date}
              </p>
              {step.status === 'ON_GOING' && step.shareLink && (
                <button style={{
                  backgroundColor: 'white',
                  border: '1px solid #e74c3c',
                  color: '#e74c3c',
                  borderRadius: '5px',
                  padding: '5px 10px',
                  marginTop: '10px',
                  cursor: 'pointer'
                }}
                  onClick={() => { window.open(step.shareLink, '_blank', 'noopener,noreferrer') }}
                >
                  <FontAwesomeIcon icon={faBicycle} style={{ marginRight: '5px' }} />
                  Track
                </button>
              )}
              {step.driver && (
                <div style={{
                  border: '1px solid #ccc',
                  borderRadius: '5px',
                  padding: '10px',
                  marginTop: '10px'
                }}>
                  <p style={{ margin: '0 0 5px 0' }}><strong>Driver Name:</strong> {step.driver.name}</p>
                  <p style={{ margin: '0 0 5px 0' }}>
                    <strong>Driver Phone:</strong> {step.driver.phone}
                    <button style={{
                      backgroundColor: 'white',
                      border: '1px solid #e74c3c',
                      borderRadius: '5px',
                      padding: '2px 5px',
                      marginLeft: '10px',
                      cursor: 'pointer'
                    }}>
                      <FontAwesomeIcon icon={faPhone} style={{ color: '#e74c3c' }} />
                    </button>
                  </p>
                  {step.driver.plateNumber && <p style={{ margin: '0' }}><strong>Number Plate:</strong> {step.driver.plateNumber}</p>}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const cancelOrder = async (e, order_id, deliveryMethod) => {
    e.preventDefault();

    const userString = localStorage.getItem('user');
    const user = JSON.parse(userString);
    const sessionId = user['session-id'];

    const msg = t("You want to cancel order ?")
    Alerts.swalOKAlert(msg).then(async res => {
      if (res === true) {
        setLoading(true);
        DiscountCouponService.UpdateStatus(sessionId, 'cancelled', order_id, deliveryMethod).then(
          (response) => {
            setLoading(false);
            if (response.data.message === "Unauthorized") {
              AuthService.logout();
              navigate("/login");
              window.location.reload();
            } else {
              Alerts.swalClickProceedAlert('Order Has Been Cancelled Successfully').then(res => {
                if (res === true) {
                  navigate("/CustomerOrders");
                }
              });

            }
          },
          (error) => {
            setLoading(false);
            const errContent =
              (error.response && error.response.data) ||
              error.message ||
              error.toString();
            Alerts.swalErrorAlert(errContent || t("Server Error, Please try again later."));
          }
        );
      }
    });
  };

  const renderCancelOrderButton = () => {
    // Check if the order status is "confirmed"
    const isOrderConfirmed = orderDetails?.status === "confirmed";

    if (isOrderConfirmed) {
      return (
        <button
          style={{
            backgroundColor: '#e74c3c',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            padding: '10px 20px',
            width: '100%',
            marginTop: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 'bold',
            cursor: 'pointer'
          }}
          onClick={(e) => cancelOrder(e, orderDetails?.order_id, orderDetails?.deliveryMethod)}
        >
          <FontAwesomeIcon icon={faTimes} style={{ marginRight: '10px' }} /> Cancel Order
        </button>
      );
    }
    return null;
  };


  // Helper function to get the appropriate icon for each status
  const getIconForStatus = (status) => {
    switch (status.toLowerCase()) {
      case 'delivered':
        return faCheckCircle;
      case 'picked by driver':
        return faShoppingBasket;
      case 'driver assigned':
        return faBicycle;
      case 'on going':
        return faFire;
      case 'assigning driver':
        return faSearch;
      case 'order received':
        return faClipboardList;
      default:
        return faClipboardList;
    }
  };

  const getButtonStyle = (status) => {
    switch (status) {
      case 'confirmed':
        return {
          backgroundColor: '#ffc107', // Yellow
          color: '#fff',
        };
      case 'processing':
        return {
          backgroundColor: '#007bff', // Blue
          color: '#fff',
        };
      case 'in_ransit':
        return {
          backgroundColor: '#6f42c1', // Purple
          color: '#fff',
        };
      case 'cancelled':
        return {
          backgroundColor: '#dc3545', // Red
          color: '#fff',
        };
      default:
        return {
          backgroundColor: 'green', // Default color
          color: '#fff',
        };
    }
  };
  
  const capitalizeStatus = (status) => {
    if (!status) return '';
    return status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
  };
  const status = orderDetails?.status;
const buttonStyle = getButtonStyle(status);

  return (
    <Fragment>
      <div className="app-main__outer">
        <div className="app-main__inner">
          <TransitionGroup>
            <CSSTransition component="div" classNames="TabsAnimation" appear={true} timeout={0} enter={false} exit={false}>
              <div>
                <div className="app-page-title">
                  <div className="page-title-wrapper">
                    <Header branch={branch} setBranch={setBranch} />
                  </div>
                </div>

                <div style={{ maxWidth: '600px', margin: '0 auto', padding: '20px', fontFamily: 'Arial, sans-serif', backgroundColor: '#f5f5f5' }}>
                  <h1 style={{ textAlign: 'center', color: '#333', marginBottom: '20px' }}>Order Details</h1>

                  {/* Order header */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <div>
                      <p style={{ margin: '5px 0', color: '#666' }}>Order ID: #{orderDetails?.order_number}</p>
                      <p style={{ margin: '5px 0', color: '#666' }}>Placed on: {new Date(orderDetails?.order_date).toLocaleString()}</p>
                    </div>
                    <button style={{
  ...buttonStyle,
  border: 'none',
  borderRadius: '5px',
  padding: '10px 20px',
  fontWeight: 'bold'
}}>
  {capitalizeStatus(status)}
</button>
                  </div>

                  {/* Message seller button */}
                  <button style={{
                    backgroundColor: '#333',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                    padding: '10px 20px',
                    width: '100%',
                    marginBottom: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold'
                  }}>
                    <FontAwesomeIcon icon={faCommentAlt} style={{ marginRight: '10px' }} /> Message Seller
                  </button>

                  {/* Details and Tracking buttons */}
                  <div style={{ display: 'flex', marginBottom: '20px' }}>
                    <button
                      style={{
                        flex: 1,
                        backgroundColor: activeTab === 'details' ? '#e74c3c' : 'white',
                        color: activeTab === 'details' ? 'white' : '#333',
                        border: activeTab === 'details' ? 'none' : '1px solid #ccc',
                        borderRadius: '5px 0 0 5px',
                        padding: '10px',
                        fontWeight: 'bold',
                        cursor: 'pointer'
                      }}
                      onClick={() => setActiveTab('details')}
                    >
                      DETAILS
                    </button>
                    <button
                      style={{
                        flex: 1,
                        backgroundColor: activeTab === 'tracking' ? '#e74c3c' : 'white',
                        color: activeTab === 'tracking' ? 'white' : '#333',
                        border: activeTab === 'tracking' ? 'none' : '1px solid #ccc',
                        borderRadius: '0 5px 5px 0',
                        padding: '10px',
                        fontWeight: 'bold',
                        cursor: 'pointer'
                      }}
                      onClick={() => setActiveTab('tracking')}
                    >
                      TRACKING
                    </button>
                  </div>
                  {activeTab === 'details' ? renderDetailsContent() : renderTrackingContentDEMO()}
                  {/* {activeTab === 'details' ? renderDetailsContent() : renderTrackingContent()} */}

                  {renderCancelOrderButton()}
                </div>



              </div>
            </CSSTransition>
          </TransitionGroup>
        </div>
      </div>
    </Fragment>
  );
};

export default Orders;